import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRentalDetail } from '../../Redux/Actions/actions';
import { 
  FaHome, 
  FaUser, 
  FaDollarSign, 
  FaCalendarAlt, 
  FaFileContract,
  FaEdit,
  FaArrowLeft,
  FaPrint,
  FaDownload,
  FaExclamationTriangle 
} from 'react-icons/fa';
import style from './RentalDetail.module.css';

const RentalDetail = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { rentalDetail, loading, error } = useSelector(state => state.rentals || {});
  const [rental, setRental] = useState(null);

  useEffect(() => {
    // Cargar detalles del contrato desde el backend real
    dispatch(fetchRentalDetail(contractId));
  }, [dispatch, contractId]);

  useEffect(() => {
    if (rentalDetail) {
      setRental(rentalDetail.rental || rentalDetail); // Manejar estructura anidada
    }
  }, [rentalDetail]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR');
  };

  const calculateContractDuration = () => {
    if (!rental?.rental?.contractStart || !rental?.rental?.contractEnd) return 'No definido';
    
    const start = new Date(rental.rental.contractStart);
    const end = new Date(rental.rental.contractEnd);
    const diffTime = end - start;
    const diffMonths = Math.round(diffTime / (1000 * 60 * 60 * 24 * 30));
    
    return `${diffMonths} meses`;
  };

  const getDaysUntilExpiry = () => {
    if (!rental?.rental?.contractEnd) return null;
    
    const today = new Date();
    const expiryDate = new Date(rental.rental.contractEnd);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  if (loading) {
    return (
      <div className={style.container}>
        <div className={style.loading}>
          <p>Cargando detalles del contrato...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={style.container}>
        <div className={style.notFound}>
          <h2>Error al cargar el contrato</h2>
          <p>{error}</p>
          <Link to="/alquileres" className={style.backButton}>
            <FaArrowLeft /> Volver a Alquileres
          </Link>
        </div>
      </div>
    );
  }

  if (!rental) {
    return (
      <div className={style.container}>
        <div className={style.notFound}>
          <h2>Contrato no encontrado</h2>
          <p>No se pudo encontrar el contrato con ID: {contractId}</p>
          <Link to="/alquileres" className={style.backButton}>
            <FaArrowLeft /> Volver a Alquileres
          </Link>
        </div>
      </div>
    );
  }

  const daysLeft = getDaysUntilExpiry();
  const isExpiring = daysLeft !== null && daysLeft <= 90 && daysLeft > 0;

  return (
    <div className={style.container}>
      {/* Header */}
      <div className={style.header}>
        <div className={style.headerLeft}>
          <button onClick={() => navigate('/alquileres')} className={style.backBtn}>
            <FaArrowLeft /> Volver
          </button>
          <div>
            <h1 className={style.title}>Detalles del Contrato</h1>
            <p className={style.subtitle}>Contrato ID: {rental.id}</p>
          </div>
        </div>
        
        <div className={style.headerActions}>
          <button className={style.actionBtn}>
            <FaPrint /> Imprimir
          </button>
          <button className={style.actionBtn}>
            <FaDownload /> Descargar PDF
          </button>
          <button className={style.actionBtn}>
            <FaEdit /> Editar
          </button>
        </div>
      </div>

      {/* Alert si está por vencer */}
      {isExpiring && (
        <div className={style.alert}>
          <FaCalendarAlt />
          <span>⚠️ Este contrato vence en {daysLeft} días ({formatDate(rental.rental.contractEnd)})</span>
        </div>
      )}

      <div className={style.content}>
        {/* Información de la Propiedad */}
        <div className={style.section}>
          <h2 className={style.sectionTitle}>
            <FaHome /> Información de la Propiedad
          </h2>
          <div className={style.grid}>
            <div className={style.infoGroup}>
              <label>Dirección</label>
              <p>{rental.street} {rental.number}, {rental.locality}, {rental.province}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Tipo de Propiedad</label>
              <p>{rental.propertyType || 'No especificado'}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Ambientes</label>
              <p>{rental.environments} ambientes</p>
            </div>
            <div className={style.infoGroup}>
              <label>Dormitorios</label>
              <p>{rental.rooms} dormitorios</p>
            </div>
            <div className={style.infoGroup}>
              <label>Baños</label>
              <p>{rental.bathrooms} baños</p>
            </div>
            <div className={style.infoGroup}>
              <label>Estado</label>
              <p>{rental.propertyState || 'No especificado'}</p>
            </div>
          </div>
        </div>

        {/* Información del Propietario */}
        <div className={style.section}>
          <h2 className={style.sectionTitle}>
            <FaUser /> Propietario
          </h2>
          <div className={style.grid}>
            <div className={style.infoGroup}>
              <label>Nombre</label>
              <p>{rental.ownerName || 'Sin asignar'}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Teléfono</label>
              <p>{rental.ownerPhone || 'No disponible'}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Email</label>
              <p>{rental.ownerEmail || 'No disponible'}</p>
            </div>
          </div>
        </div>

        {/* Información del Contrato */}
        <div className={style.section}>
          <h2 className={style.sectionTitle}>
            <FaFileContract /> Detalles del Contrato
          </h2>
          <div className={style.grid}>
            <div className={style.infoGroup}>
              <label>Fecha de Inicio</label>
              <p>{rental.rental?.contractStart ? formatDate(rental.rental.contractStart) : 'No definido'}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Fecha de Finalización</label>
              <p>{rental.rental?.contractEnd ? formatDate(rental.rental.contractEnd) : 'No definido'}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Duración</label>
              <p>{calculateContractDuration()}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Día de Pago</label>
              <p>Día {rental.rental?.paymentDueDay || 10} de cada mes</p>
            </div>
            <div className={style.infoGroup}>
              <label>Tipo de Ajuste</label>
              <p>{(rental.rental?.adjustmentType || 'ICL').toUpperCase()}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Frecuencia de Ajuste</label>
              <p>Cada {rental.rental?.adjustmentFrequency || 12} meses</p>
            </div>
          </div>
        </div>

        {/* Información Financiera */}
        <div className={style.section}>
          <h2 className={style.sectionTitle}>
            <FaDollarSign /> Información Financiera
          </h2>
          <div className={style.grid}>
            <div className={style.infoGroup}>
              <label>Valor Inicial del Alquiler</label>
              <p className={style.price}>
                {formatCurrency(rental.rental?.rentValue || rental.price || 0)}
                <span className={style.currency}> {rental.rental?.currency || rental.currency || 'ARS'}</span>
              </p>
            </div>
            <div className={style.infoGroup}>
              <label>Valor Actual del Alquiler</label>
              <p className={style.price}>
                {formatCurrency(rental.rental?.currentRentValue || rental.rental?.rentValue || rental.price || 0)}
                <span className={style.currency}> {rental.rental?.currency || rental.currency || 'ARS'}</span>
              </p>
            </div>
            <div className={style.infoGroup}>
              <label>Depósito</label>
              <p>{formatCurrency(rental.rental?.deposit || 0)}</p>
            </div>
            <div className={style.infoGroup}>
              <label>Comisión de Admin.</label>
              <p>{rental.rental?.adminCommission || 0}%</p>
            </div>
          </div>
        </div>

        {/* Acciones rápidas */}
        <div className={style.section}>
          <h2 className={style.sectionTitle}>Acciones</h2>
          <div className={style.quickActions}>
            <Link 
              to={`/alquileres/contrato/${rental.id}/pagos`}
              className={style.quickActionBtn}
            >
              <FaDollarSign />
              <span>Ver Pagos</span>
            </Link>
            <Link 
              to={`/alquileres/contrato/${rental.id}/inquilinos`}
              className={style.quickActionBtn}
            >
              <FaUser />
              <span>Gestionar Inquilinos</span>
            </Link>
            <button className={style.quickActionBtn}>
              <FaFileContract />
              <span>Renovar Contrato</span>
            </button>
            <button className={style.quickActionBtn}>
              <FaEdit />
              <span>Modificar Términos</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalDetail;